/* global gtag, fbq */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, MapPin, Clock, Star, Users, Smartphone, Heart, Menu, X, Gift } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { database } from '../firebaseConfig';
import { ref, push, set } from 'firebase/database';

export default function BuySanJuanEs() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [popupEmail, setPopupEmail] = useState('');
  const [popupSubmitting, setPopupSubmitting] = useState(false);
  const [popupSuccess, setPopupSuccess] = useState(false);
  const [popupError, setPopupError] = useState('');
  const [codeCopied, setCodeCopied] = useState(false);
  const [riddleAnswer, setRiddleAnswer] = useState('');
  const [riddleSolved, setRiddleSolved] = useState(false);
  const [riddleHints, setRiddleHints] = useState(0);
  const [riddleShake, setRiddleShake] = useState(false);
  const [demoStep, setDemoStep] = useState(0);
  const [showHistoric, setShowHistoric] = useState(false);

  // Exit-intent popup - scroll-up on mobile after 45 seconds
  useEffect(() => {
    const hasInteracted = localStorage.getItem('exitPopupDismissed_sanJuanEs');
    if (hasInteracted) return;

    let timeReached = false;
    let lastScrollY = window.scrollY;

    const timer = setTimeout(() => {
      timeReached = true;
    }, 45000);

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (timeReached && currentScrollY < lastScrollY && currentScrollY > 100) {
        setShowExitPopup(true);
        if (typeof gtag !== 'undefined') {
          gtag('event', 'exit_popup_shown', { page: 'san-juan-es' });
        }
        window.removeEventListener('scroll', handleScroll);
      }
      lastScrollY = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handlePopupClose = () => {
    setShowExitPopup(false);
    localStorage.setItem('exitPopupDismissed_sanJuanEs', 'true');
    if (typeof gtag !== 'undefined') {
      gtag('event', 'exit_popup_dismissed', {
        page: 'san-juan-es'
      });
    }
  };

  const handlePopupSubmit = async (e) => {
    e.preventDefault();
    if (!popupEmail || popupSubmitting) return;

    setPopupSubmitting(true);
    setPopupError('');

    try {
      const emailCapturesRef = ref(database, 'emailCaptures');
      const newEmailRef = push(emailCapturesRef);
      await set(newEmailRef, {
        email: popupEmail,
        source: 'exit_popup',
        page: 'san-juan-es',
        tourId: 'san-juan-es',
        promoCode: 'EXPLORE2026',
        language: 'es',
        createdAt: new Date().toISOString()
      });

      setPopupSuccess(true);
      localStorage.setItem('exitPopupDismissed_sanJuanEs', 'submitted');

      if (typeof gtag !== 'undefined') {
        gtag('event', 'exit_popup_submitted', {
          page: 'san-juan-es'
        });
      }
      if (typeof fbq !== 'undefined') {
        fbq('track', 'Lead', {
          content_name: 'Exit Popup Email Capture',
          content_category: 'san-juan-es'
        });
      }
    } catch (error) {
      console.error('Error submitting email:', error);
      setPopupError('Algo salió mal. Por favor, inténtalo de nuevo.');
    } finally {
      setPopupSubmitting(false);
    }
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText('EXPLORE2026');
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = 'EXPLORE2026';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    }
  };

  // Show sticky CTA on scroll, hide near bottom
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const nearBottom = scrollY + window.innerHeight > document.documentElement.scrollHeight - 300;
      setShowStickyCTA(scrollY > 300 && !nearBottom);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleBuyNow = (location) => {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'begin_checkout', {
        currency: 'USD',
        value: 26.99,
        items: [{
          item_id: 'san-juan-tour-es',
          item_name: 'San Juan Scavenger Hunt Tour (Spanish)',
          price: 26.99,
          quantity: 1
        }],
        cta_location: location
      });
    }
    if (typeof fbq !== 'undefined') {
      fbq('track', 'InitiateCheckout', {
        content_ids: ['san-juan-tour-es'],
        content_type: 'product',
        value: 26.99,
        currency: 'USD'
      });
    }
    const promoCode = location === 'popup' ? 'EXPLORE2026' : 'SAVE10';
    window.location.href = `https://buy.stripe.com/8x23coe3A3Z65tB0ylcZa01?prefilled_promo_code=${promoCode}`;
  };

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Tour de Búsqueda del Tesoro en San Juan Puerto Rico - Tour in a Box</title>
        <meta name="description" content="Explora el Viejo San Juan con un tour interactivo de búsqueda del tesoro. Resuelve acertijos en 12 lugares históricos durante 2 horas. A tu propio ritmo, funciona sin conexión. ¡Reserva ahora por $26.99!" />
        <meta name="keywords" content="búsqueda del tesoro San Juan, tour a pie Viejo San Juan, caza del tesoro Puerto Rico, acertijos San Juan, tour autoguiado San Juan, qué hacer en Viejo San Juan" />
        <link rel="canonical" href="https://play.tourinabox.com/buy/san-juan-es" />
        <html lang="es" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="product" />
        <meta property="og:url" content="https://play.tourinabox.com/buy/san-juan-es" />
        <meta property="og:title" content="Tour de Búsqueda del Tesoro en San Juan - Tour Interactivo a Pie" />
        <meta property="og:description" content="Resuelve acertijos en 12 lugares históricos del Viejo San Juan. Tour a tu propio ritmo. Funciona sin conexión. Perfecto para parejas, familias y aventureros." />
        <meta property="og:image" content="https://play.tourinabox.com/images/sanjuan.webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Calles coloridas del Viejo San Juan, Puerto Rico" />
        <meta property="og:site_name" content="Tour in a Box" />
        <meta property="og:locale" content="es_ES" />
        <meta property="product:price:amount" content="26.99" />
        <meta property="product:price:currency" content="USD" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://play.tourinabox.com/buy/san-juan-es" />
        <meta name="twitter:title" content="Tour de Búsqueda del Tesoro en San Juan" />
        <meta name="twitter:description" content="Tour interactivo a pie del Viejo San Juan. Resuelve acertijos en 12 lugares históricos. A tu ritmo, sin conexión. ¡Reserva tu aventura!" />
        <meta name="twitter:image" content="https://play.tourinabox.com/images/sanjuan.webp" />
        <meta name="twitter:image:alt" content="Calles coloridas del Viejo San Juan, Puerto Rico" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org/",
            "@type": "Product",
            "name": "Tour de Búsqueda del Tesoro en San Juan",
            "description": "Tour interactivo a pie del Viejo San Juan con 12 paradas basadas en acertijos a través de lugares históricos. Experiencia de búsqueda del tesoro a tu propio ritmo.",
            "brand": {
              "@type": "Brand",
              "name": "Tour in a Box"
            },
            "offers": {
              "@type": "Offer",
              "url": "https://play.tourinabox.com/buy/san-juan-es",
              "priceCurrency": "USD",
              "price": "26.99",
              "priceValidUntil": "2026-12-31",
              "availability": "https://schema.org/InStock",
              "seller": {
                "@type": "Organization",
                "name": "Tour in a Box LLC"
              }
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "reviewCount": "150",
              "bestRating": "5",
              "worstRating": "1"
            },
            "image": "https://play.tourinabox.com/images/sanjuan.webp",
            "category": "Tours & Activities",
            "inLanguage": "es"
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Inicio",
                "item": "https://play.tourinabox.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Tour San Juan (Español)",
                "item": "https://play.tourinabox.com/buy/san-juan-es"
              }
            ]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white" style={{ fontFamily: 'Urbanist, sans-serif' }}>
      {/* Header with Logo */}
      <header className="pt-8 pb-6 px-4 border-b border-gray-200" style={{ background: 'white' }}>
        <div className="max-w-7xl mx-auto relative flex items-center justify-between">
          {/* Language Toggle - left */}
          <div>
            <Link
              to="/buy/san-juan"
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors hover:bg-gray-100"
              style={{ color: '#413C04', border: '1px solid #E4B4A6' }}
            >
              <span>🇺🇸</span>
              <span>English</span>
            </Link>
          </div>

          {/* Logo - absolutely centered */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            <img
              src="/PrimaryLogo.png"
              alt="Tour in a Box"
              className="h-[40px] sm:h-[48px]"
              style={{ objectFit: 'contain', width: 'auto' }}
            />
          </div>

          {/* Menu Button - right */}
          <div className="relative">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Menú"
            >
              <Menu size={18} style={{ color: '#413C04' }} />
            </button>

            {/* Dropdown Menu */}
            {menuOpen && (
              <div
                className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
                style={{ fontFamily: 'Urbanist, sans-serif' }}
              >
                <a
                  href="https://www.tourinabox.com/store"
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  Ver Todos los Tours
                </a>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(to bottom right, #FFFADF, rgba(255, 250, 223, 0.5))' }}>
        {/* Background pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: "url(/images/noise-512.png)" }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12 sm:pt-12 sm:pb-20">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Video - Shows first on mobile, second on desktop */}
            <div className="relative order-first md:order-last">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <video
                  src="/videos/san-juan-hero.mp4"
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-auto"
                  poster="/images/sanjuan.webp"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent pointer-events-none"></div>
              </div>
            </div>

            {/* Copy - Shows second on mobile, first on desktop */}
            <div className="order-last md:order-first">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-2" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                San Juan, Puerto Rico
              </h1>

              <p className="text-2xl font-bold mb-3" style={{ fontFamily: 'Rubik, sans-serif', color: '#E4B4A6' }}>
                Tour de Búsqueda del Tesoro
              </p>

              {/* Social Proof - moved up */}
              <div
                className="flex items-center gap-4 mb-6 cursor-pointer transition-opacity hover:opacity-80"
                onClick={() => {
                  document.getElementById('testimonials')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              >
                <div className="flex -space-x-2">
                  <img src="/images/customer1.png" alt="" className="w-8 h-8 rounded-full border-2 border-white object-cover" />
                  <img src="/images/customer2.png" alt="" className="w-8 h-8 rounded-full border-2 border-white object-cover" />
                  <img src="/images/customer3.png" alt="" className="w-8 h-8 rounded-full border-2 border-white object-cover" />
                </div>
                <div className="text-sm">
                  <div className="flex items-center gap-1 mb-1">
                    <div className="flex items-center gap-1" style={{ color: '#F8662B' }}>
                      <Star size={14} fill="currentColor" />
                      <Star size={14} fill="currentColor" />
                      <Star size={14} fill="currentColor" />
                      <Star size={14} fill="currentColor" />
                      <Star size={14} fill="currentColor" />
                    </div>
                    <span className="font-semibold" style={{ color: '#413C04' }}>4.8 de 5</span>
                  </div>
                  <p style={{ color: '#413C04', opacity: 0.7 }}>
                    Amado por <span className="font-semibold" style={{ color: '#413C04', opacity: 1 }}>locales y visitantes</span>
                  </p>
                </div>
              </div>

              <p className="text-lg mb-6" style={{ color: '#413C04', opacity: 0.75 }}>
                Resuelve acertijos, desbloquea secretos y descubre el Viejo San Juan de una manera completamente nueva. Tu teléfono se convierte en tu guía mientras exploras fuertes históricos, monumentos ocultos y lugares favoritos locales.
              </p>

              {/* Key Benefits */}
              <div className="space-y-3 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(56, 91, 55, 0.15)' }}>
                    <Check size={16} style={{ color: '#385B37' }} />
                  </div>
                  <span style={{ color: '#413C04' }}>12 paradas históricas con acertijos por resolver</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(56, 91, 55, 0.15)' }}>
                    <Check size={16} style={{ color: '#385B37' }} />
                  </div>
                  <span style={{ color: '#413C04' }}>Aventura de 2 horas a tu propio ritmo</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(56, 91, 55, 0.15)' }}>
                    <Check size={16} style={{ color: '#385B37' }} />
                  </div>
                  <span style={{ color: '#413C04' }}>Juega al instante o en cualquier momento del próximo año</span>
                </div>
              </div>

              {/* Pricing CTA */}
              <div className="bg-white rounded-2xl shadow-xl p-6 border-2" style={{ borderColor: '#E4B4A6' }}>
                <div className="flex items-baseline justify-center gap-3 mb-4">
                  <span className="text-3xl font-bold" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>$26.99</span>
                  <span className="text-xl text-gray-400 line-through">$30</span>
                  <span className="bg-red-100 text-red-700 text-sm font-bold px-2 py-1 rounded">
                    Ahorra 10%
                  </span>
                </div>

                <button
                  onClick={() => handleBuyNow('hero')}
                  className="w-full text-white font-bold py-4 px-8 rounded-xl text-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                  style={{
                    background: '#F8662B',
                    fontFamily: 'Rubik, sans-serif'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#E05520'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#F8662B'}
                >
                  Obtener Mi Tour
                </button>

                <p className="text-sm text-center mt-3 mb-2" style={{ color: '#413C04', opacity: 0.7 }}>
                  Compra ahora, juega en cualquier momento dentro de un año
                </p>

                <div className="flex items-center justify-center gap-4 text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
                  <span className="flex items-center gap-1">
                    <Check size={16} style={{ color: '#385B37' }} />
                    1 año de acceso
                  </span>
                  <span className="flex items-center gap-1">
                    <Check size={16} style={{ color: '#385B37' }} />
                    Reembolso 30 días
                  </span>
                </div>
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* Try It — Interactive Demo */}
      <section className="py-16" style={{ background: 'linear-gradient(to bottom, white, #FFFADF)' }}>
        <div className="max-w-lg mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-3" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
            Prueba una Parada
          </h2>
          <p className="text-center mb-8" style={{ color: '#413C04', opacity: 0.7 }}>
            Mira cómo funciona el tour con un acertijo de muestra
          </p>

          {/* Phone-style frame */}
          <div className="rounded-3xl overflow-hidden shadow-2xl border-4" style={{ borderColor: '#413C04', background: '#413C04' }}>
            {/* Phone top bar */}
            <div className="flex items-center justify-between px-4 py-2" style={{ background: '#413C04' }}>
              <span className="text-xs font-semibold" style={{ color: 'rgba(255,255,255,0.6)' }}>Ubicación Actual: El Morro</span>
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(248, 102, 43, 0.3)', color: '#F8662B' }}>Parada 7 de 12</span>
            </div>

            {/* Screen content */}
            <div className="bg-white px-5 py-6" style={{ minHeight: 320 }}>

              {/* Card 0: Riddle */}
              {demoStep === 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'rgba(248, 102, 43, 0.12)' }}>
                      <Star size={16} style={{ color: '#F8662B' }} />
                    </div>
                    <span className="text-sm font-bold" style={{ color: '#F8662B', fontFamily: 'Rubik, sans-serif' }}>ACERTIJO</span>
                  </div>

                  <p className="text-lg mb-6 leading-relaxed" style={{ color: '#413C04' }}>
                    Corona los acantilados con poder callado,<br />
                    Un ojo giratorio en la noche ha brillado.<br />
                    Sin voz, sin llama, pero advierte al mar,<br />
                    ¿Qué guía a los barcos para no naufragar?
                  </p>

                  <div className={`mb-4 ${riddleShake ? 'animate-shake' : ''}`}>
                    <input
                      type="text"
                      value={riddleAnswer}
                      onChange={(e) => setRiddleAnswer(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const a = riddleAnswer.trim().toLowerCase();
                          if (a.includes('lighthouse') || a.includes('faro') || a.includes('light house')) {
                            setRiddleSolved(true);
                            setDemoStep(1);
                          } else {
                            setRiddleShake(true);
                            setTimeout(() => setRiddleShake(false), 500);
                          }
                        }
                      }}
                      placeholder="Escribe tu respuesta..."
                      className="w-full px-4 py-3 rounded-xl text-base outline-none"
                      style={{ border: '2px solid #E4B4A6', color: '#413C04', fontFamily: 'Urbanist, sans-serif' }}
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        const a = riddleAnswer.trim().toLowerCase();
                        if (a.includes('lighthouse') || a.includes('faro') || a.includes('light house')) {
                          setRiddleSolved(true);
                          setDemoStep(1);
                        } else {
                          setRiddleShake(true);
                          setTimeout(() => setRiddleShake(false), 500);
                        }
                      }}
                      className="flex-1 text-white font-bold py-3 rounded-xl transition-all"
                      style={{ background: '#F8662B', fontFamily: 'Rubik, sans-serif' }}
                    >
                      Enviar
                    </button>
                    <button
                      onClick={() => setRiddleHints(h => Math.min(h + 1, 2))}
                      className="px-4 py-3 rounded-xl font-semibold transition-all"
                      style={{ border: '2px solid #E4B4A6', color: '#413C04', background: 'white' }}
                    >
                      {riddleHints === 0 ? '¿Pista?' : riddleHints === 1 ? '¿Más?' : 'Última'}
                    </button>
                  </div>

                  {riddleHints >= 1 && (
                    <div className="mt-4 p-3 rounded-lg text-sm" style={{ background: 'rgba(56, 91, 55, 0.08)', color: '#385B37' }}>
                      💡 Se encuentra encima de El Morro, guiando a los marineros al puerto
                    </div>
                  )}
                  {riddleHints >= 2 && (
                    <div className="mt-2 p-3 rounded-lg text-sm" style={{ background: 'rgba(56, 91, 55, 0.08)', color: '#385B37' }}>
                      💡 Su luz se puede ver por millas a través del océano por la noche
                    </div>
                  )}
                  {riddleHints >= 2 && (
                    <button
                      onClick={() => {
                        setRiddleSolved(true);
                        setDemoStep(1);
                      }}
                      className="mt-3 text-sm font-semibold underline"
                      style={{ color: '#413C04', opacity: 0.5 }}
                    >
                      Revelar respuesta
                    </button>
                  )}
                </div>
              )}

              {/* Card 1: Solved! */}
              {demoStep === 1 && (
                <div>
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'rgba(56, 91, 55, 0.15)' }}>
                      <Check size={16} style={{ color: '#385B37' }} />
                    </div>
                    <h3 className="text-xl font-bold" style={{ fontFamily: 'Rubik, sans-serif', color: '#385B37' }}>
                      ¡Correcto!
                    </h3>
                  </div>
                  <p className="font-semibold mb-3 text-center" style={{ color: '#413C04', fontFamily: 'Rubik, sans-serif' }}>El Faro del Morro</p>
                  <div className="flex gap-3 mb-3">
                    <div className="relative rounded-xl overflow-hidden flex-shrink-0" style={{ aspectRatio: '3/4', width: '40%' }}>
                      <img
                        src="/images/ElMorroLight.jpg"
                        alt="El Faro del Morro hoy"
                        className="w-full h-full object-cover absolute inset-0 transition-opacity duration-500"
                        style={{ opacity: showHistoric ? 0 : 1 }}
                      />
                      <img
                        src="/images/ElMorro-historic.jpg"
                        alt="El Faro del Morro circa 1800s"
                        className="w-full h-full object-cover absolute inset-0 transition-opacity duration-500"
                        style={{ opacity: showHistoric ? 1 : 0 }}
                      />
                      <div className="absolute bottom-2 right-2 flex rounded-full overflow-hidden" style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}>
                        <button
                          onClick={() => setShowHistoric(false)}
                          className="px-2.5 py-1 text-xs font-bold transition-all"
                          style={{ background: !showHistoric ? 'white' : 'transparent', color: !showHistoric ? '#413C04' : 'white', borderRadius: '9999px' }}
                        >
                          Hoy
                        </button>
                        <button
                          onClick={() => setShowHistoric(true)}
                          className="px-2.5 py-1 text-xs font-bold transition-all"
                          style={{ background: showHistoric ? 'white' : 'transparent', color: showHistoric ? '#413C04' : 'white', borderRadius: '9999px' }}
                        >
                          1800s
                        </button>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold uppercase tracking-wide mb-1" style={{ color: '#F8662B' }}>Aprende sobre este sitio</p>
                      <ul className="text-sm space-y-1" style={{ color: '#413C04', opacity: 0.7 }}>
                        <li>• Antes del faro, El Morro usaba faroles para advertir a los barcos</li>
                        <li>• España construyó el original en 1846, el primer faro de todo Puerto Rico</li>
                        <li>• La Marina de EE.UU. lo destruyó en 1898 durante la Guerra Hispanoamericana</li>
                        <li>• Reconstruido en 1908, aún destella cada 15 segundos para guiar barcos al puerto</li>
                      </ul>
                    </div>
                  </div>
                  <button
                    onClick={() => setDemoStep(2)}
                    className="w-full text-white font-bold py-3 rounded-xl transition-all"
                    style={{ background: '#F8662B', fontFamily: 'Rubik, sans-serif' }}
                  >
                    Ver qué sigue →
                  </button>
                </div>
              )}

              {/* Card 2: Directions preview */}
              {demoStep === 2 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'rgba(143, 179, 211, 0.2)' }}>
                      <MapPin size={16} style={{ color: '#8FB3D3' }} />
                    </div>
                    <span className="text-sm font-bold" style={{ color: '#8FB3D3', fontFamily: 'Rubik, sans-serif' }}>DIRECCIONES A PARADA 8</span>
                  </div>

                  <p className="text-lg mb-4 leading-relaxed" style={{ color: '#413C04' }}>
                    Con El Morro a tu espalda, busca los <strong>edificios amarillos con cúpula roja</strong>. Sigue el camino a lo largo del océano hasta llegar a una <strong>estatua con cuatro figuras de bronce</strong>.
                  </p>

                  <div className="p-4 rounded-xl mb-4" style={{ background: 'rgba(143, 179, 211, 0.1)', border: '1px solid rgba(143, 179, 211, 0.3)' }}>
                    <div className="flex items-center gap-2 mb-1">
                      <Clock size={14} style={{ color: '#8FB3D3' }} />
                      <span className="text-sm font-semibold" style={{ color: '#413C04' }}>5 min caminando</span>
                    </div>
                    <p className="text-xs" style={{ color: '#413C04', opacity: 0.6 }}>
                      Navega por puntos de referencia, con GPS de respaldo si lo necesitas
                    </p>
                  </div>

                  <button
                    onClick={() => setDemoStep(3)}
                    className="w-full text-white font-bold py-3 rounded-xl transition-all"
                    style={{ background: '#F8662B', fontFamily: 'Rubik, sans-serif' }}
                  >
                    Una cosa más →
                  </button>
                </div>
              )}

              {/* Card 3: Local recommendations */}
              {demoStep === 3 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'rgba(248, 102, 43, 0.12)' }}>
                      <Heart size={16} style={{ color: '#F8662B' }} />
                    </div>
                    <span className="text-sm font-bold" style={{ color: '#F8662B', fontFamily: 'Rubik, sans-serif' }}>LUGARES LOCALES CERCA DE TI</span>
                  </div>

                  <p className="text-sm mb-4" style={{ color: '#413C04', opacity: 0.7 }}>
                    En cada parada, también desbloqueas recomendaciones locales:
                  </p>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: 'rgba(255, 250, 223, 0.8)', border: '1px solid rgba(228, 180, 166, 0.3)' }}>
                      <span className="text-xl">☕</span>
                      <div>
                        <p className="text-sm font-semibold" style={{ color: '#413C04' }}>Café Don Ruiz</p>
                        <p className="text-xs" style={{ color: '#413C04', opacity: 0.6 }}>Espresso puertorriqueño de origen único y mallorcas tostadas dentro de Ballajá</p>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => handleBuyNow('demo')}
                    className="w-full text-white font-bold py-3 rounded-xl transition-all"
                    style={{ background: '#385B37', fontFamily: 'Rubik, sans-serif' }}
                  >
                    Obtener el tour completo de 12 paradas →
                  </button>
                  <button
                    onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                    className="w-full mt-2 text-sm font-semibold py-2 transition-colors"
                    style={{ color: '#413C04', opacity: 0.5 }}
                  >
                    Seguir explorando ↓
                  </button>
                </div>
              )}
            </div>

            {/* Progress dots */}
            <div className="flex items-center justify-center gap-2 py-3" style={{ background: '#413C04' }}>
              {[0, 1, 2, 3].map(i => (
                <button
                  key={i}
                  onClick={() => { if (i === 0 || riddleSolved) setDemoStep(i); }}
                  className="rounded-full transition-all duration-300"
                  style={{
                    width: demoStep === i ? 24 : 8,
                    height: 8,
                    background: demoStep === i ? '#F8662B' : 'rgba(255,255,255,0.3)',
                    cursor: (i === 0 || riddleSolved) ? 'pointer' : 'default'
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        <style>{`
          @keyframes shake {
            0%, 100% { transform: translateX(0); }
            25% { transform: translateX(-8px); }
            75% { transform: translateX(8px); }
          }
          .animate-shake { animation: shake 0.3s ease-in-out; }
        `}</style>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
            Por Qué los Exploradores Aman Este Tour
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(143, 179, 211, 0.2)' }}>
                <MapPin size={24} style={{ color: '#8FB3D3' }} />
              </div>
              <div>
                <h3 className="font-bold mb-1" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Direcciones por Puntos de Referencia</h3>
                <p className="text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
                  Navega usando puntos de referencia. Mirarás hacia arriba y realmente notarás la ciudad.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(56, 91, 55, 0.15)' }}>
                <Clock size={24} style={{ color: '#385B37' }} />
              </div>
              <div>
                <h3 className="font-bold mb-1" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>A Tu Propio Ritmo</h3>
                <p className="text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
                  Pausa en cualquier momento para fotos, comida o compras. Tu progreso se guarda automáticamente.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(248, 102, 43, 0.15)' }}>
                <Smartphone size={24} style={{ color: '#F8662B' }} />
              </div>
              <div>
                <h3 className="font-bold mb-1" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Funciona Sin Conexión</h3>
                <p className="text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
                  Descarga una vez y úsalo sin conexión a internet.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(248, 102, 43, 0.15)' }}>
                <Star size={24} style={{ color: '#F8662B' }} />
              </div>
              <div>
                <h3 className="font-bold mb-1" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Acertijos Cautivadores</h3>
                <p className="text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
                  Desafíos divertidos en cada parada con pistas si las necesitas.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(228, 180, 166, 0.4)' }}>
                <Users size={24} style={{ color: '#E4B4A6' }} />
              </div>
              <div>
                <h3 className="font-bold mb-1" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Perfecto para Todos</h3>
                <p className="text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
                  Ideal para parejas, familias, amigos o aventureros solitarios.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(143, 179, 211, 0.2)' }}>
                <Heart size={24} style={{ color: '#8FB3D3' }} />
              </div>
              <div>
                <h3 className="font-bold mb-1" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Recomendaciones Locales</h3>
                <p className="text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
                  Descubre cafés, restaurantes y lugares ocultos en cada parada.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-16" style={{ background: 'linear-gradient(to bottom right, rgba(255, 250, 223, 0.6), rgba(228, 180, 166, 0.4))' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
            Lo Que Dicen los Exploradores
          </h2>
          <p className="text-center mb-12" style={{ color: '#413C04', opacity: 0.7 }}>
            Reseñas reales de exploradores reales
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Testimonial 1 */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center gap-1 mb-3" style={{ color: '#F8662B' }}>
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
              </div>
              <p className="mb-4 italic" style={{ color: '#413C04', opacity: 0.8 }}>
                "A nuestro grupo le encantó la sensación de búsqueda del tesoro del tour de San Juan. Gran idea y producto. 10/10."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full" style={{ background: 'linear-gradient(to bottom right, #8FB3D3, #6A92B3)' }}></div>
                <div>
                  <div className="font-semibold" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Cliente Verificado</div>
                  <div className="text-sm" style={{ color: '#413C04', opacity: 0.6 }}>Tour San Juan</div>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center gap-1 mb-3" style={{ color: '#F8662B' }}>
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
              </div>
              <p className="mb-4 italic" style={{ color: '#413C04', opacity: 0.8 }}>
                "Nos encantó la pequeña sorpresa al final. Compraría un tour para cada ciudad que visite."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full" style={{ background: 'linear-gradient(to bottom right, #385B37, #2A4428)' }}></div>
                <div>
                  <div className="font-semibold" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Cliente Verificado</div>
                  <div className="text-sm" style={{ color: '#413C04', opacity: 0.6 }}>Tour San Juan</div>
                </div>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center gap-1 mb-3" style={{ color: '#F8662B' }}>
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
              </div>
              <p className="mb-4 italic" style={{ color: '#413C04', opacity: 0.8 }}>
                "Me encantó usar direcciones basadas en puntos de referencia. Nos obligó a mirar hacia arriba y notar la ciudad."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full" style={{ background: 'linear-gradient(to bottom right, #F8662B, #E05520)' }}></div>
                <div>
                  <div className="font-semibold" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Cliente Verificado</div>
                  <div className="text-sm" style={{ color: '#413C04', opacity: 0.6 }}>Tour San Juan</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
            Preguntas Frecuentes
          </h2>

          <style>{`
            details summary {
              list-style: none;
              position: relative;
              padding-right: 2rem;
            }
            details summary::-webkit-details-marker {
              display: none;
            }
            details summary::after {
              content: '+';
              position: absolute;
              right: 0;
              top: 50%;
              transform: translateY(-50%);
              font-size: 1.5rem;
              font-weight: bold;
              color: #413C04;
              transition: transform 0.2s;
            }
            details[open] summary::after {
              content: '−';
            }
          `}</style>

          <div className="space-y-4">
            <details className="bg-white rounded-lg shadow-sm border p-4" style={{ borderColor: '#E4B4A6' }}>
              <summary className="font-semibold cursor-pointer" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                ¿Dónde comienza el tour y cuánto tiempo dura?
              </summary>
              <p className="mt-2" style={{ color: '#413C04', opacity: 0.7 }}>
                El tour comienza en el Puerto de Cruceros del Viejo San Juan. Una vez que compres, recibirás instrucciones detalladas sobre el punto de inicio exacto. Típicamente toma alrededor de 2 horas, ¡pero puedes ir a tu propio ritmo! Toma descansos para fotos, comida o compras. Tu progreso se guarda automáticamente.
              </p>
            </details>

            <details className="bg-white rounded-lg shadow-sm border p-4" style={{ borderColor: '#E4B4A6' }}>
              <summary className="font-semibold cursor-pointer" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                ¿Necesito descargar una aplicación?
              </summary>
              <p className="mt-2" style={{ color: '#413C04', opacity: 0.7 }}>
                ¡No! El tour funciona directamente en tu navegador web en cualquier smartphone. También puedes agregarlo a tu pantalla de inicio para acceso sin conexión.
              </p>
            </details>

            <details className="bg-white rounded-lg shadow-sm border p-4" style={{ borderColor: '#E4B4A6' }}>
              <summary className="font-semibold cursor-pointer" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                ¿Cuándo expira mi acceso?
              </summary>
              <p className="mt-2" style={{ color: '#413C04', opacity: 0.7 }}>
                Tu acceso es válido por 1 año completo desde la compra. Comienza el tour cuando estés listo - ¡hoy, la próxima semana o el próximo mes!
              </p>
            </details>

            <details className="bg-white rounded-lg shadow-sm border p-4" style={{ borderColor: '#E4B4A6' }}>
              <summary className="font-semibold cursor-pointer" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                ¿Pueden varias personas usar una compra?
              </summary>
              <p className="mt-2" style={{ color: '#413C04', opacity: 0.7 }}>
                Cada compra desbloquea acceso para una cuenta. Grupos de 3 o menos generalmente comparten un dispositivo, pero grupos más grandes a menudo prefieren tener acceso en múltiples dispositivos para que todos puedan participar.
              </p>
            </details>

            <details className="bg-white rounded-lg shadow-sm border p-4" style={{ borderColor: '#E4B4A6' }}>
              <summary className="font-semibold cursor-pointer" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                ¿Puedo regalarlo a alguien?
              </summary>
              <p className="mt-2" style={{ color: '#413C04', opacity: 0.7 }}>
                ¡Sí! Ofrecemos certificados de regalo, perfectos para viajeros. <a href="mailto:hello@tourinabox.com" className="underline font-semibold" style={{ color: '#F8662B' }}>Envíanos un email</a> para comprar uno.
              </p>
            </details>

            <details className="bg-white rounded-lg shadow-sm border p-4" style={{ borderColor: '#E4B4A6' }}>
              <summary className="font-semibold cursor-pointer" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                ¿En qué idiomas está disponible?
              </summary>
              <p className="mt-2" style={{ color: '#413C04', opacity: 0.7 }}>
                El tour está disponible en español e inglés. <Link to="/buy/san-juan" className="underline font-semibold" style={{ color: '#F8662B' }}>Haz clic aquí para la versión en inglés</Link>.
              </p>
            </details>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16" style={{ background: '#F8662B' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Rubik, sans-serif' }}>
            ¿Listo para una Aventura?
          </h2>
          <p className="text-xl mb-8" style={{ color: 'rgba(255, 255, 255, 0.95)' }}>
            Únete a los aventureros que han descubierto el Viejo San Juan de una manera completamente nueva
          </p>

          <div className="bg-white rounded-2xl p-6 max-w-md mx-auto shadow-2xl">
            <div className="flex items-baseline justify-center gap-3 mb-2">
              <span className="text-4xl font-bold" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>$26.99</span>
              <span className="text-2xl text-gray-400 line-through">$30</span>
            </div>
            <p className="text-xs text-center mb-4" style={{ color: '#413C04', opacity: 0.6 }}>
              Descuento aplicado automáticamente al pagar
            </p>
            <button
              onClick={() => handleBuyNow('bottom')}
              className="w-full text-white font-bold py-4 px-8 rounded-xl text-lg shadow-lg transition-all transform hover:scale-105 mb-4"
              style={{
                background: '#413C04',
                fontFamily: 'Rubik, sans-serif'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#2A2702'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#413C04'}
            >
              Obtener Mi Tour
            </button>
            <p className="text-sm text-center mt-2 mb-3" style={{ color: '#413C04', opacity: 0.7 }}>
              Compra ahora, juega en cualquier momento dentro de un año
            </p>
            <div className="flex items-center justify-center gap-4 text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
              <span>✓ 1 año de acceso</span>
              <span>✓ Reembolso 30 días</span>
            </div>
          </div>

          <p className="mt-6 text-sm" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            Pago seguro con Stripe
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8" style={{ background: '#413C04' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="text-sm" style={{ color: 'rgba(255, 250, 223, 0.7)' }}>
              © {new Date().getFullYear()} Tour in a Box LLC
            </div>
            <div className="flex gap-6 text-sm items-center">
              <a href="mailto:hello@tourinabox.com" className="transition" style={{ color: 'rgba(255, 250, 223, 0.7)' }} onMouseEnter={(e) => e.currentTarget.style.color = '#FFFADF'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 250, 223, 0.7)'}>
                Contacto
              </a>
              <a href="https://www.tourinabox.com/policies" target="_blank" rel="noopener noreferrer" className="transition" style={{ color: 'rgba(255, 250, 223, 0.7)' }} onMouseEnter={(e) => e.currentTarget.style.color = '#FFFADF'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 250, 223, 0.7)'}>
                Privacidad
              </a>
              <a href="https://www.instagram.com/unboxyourtour" target="_blank" rel="noopener noreferrer" className="transition" style={{ color: 'rgba(255, 250, 223, 0.7)' }} onMouseEnter={(e) => e.currentTarget.style.color = '#FFFADF'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 250, 223, 0.7)'}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="20" x="2" y="2" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/></svg>
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Mobile-Only Sticky Bottom CTA */}
      {showStickyCTA && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-50" style={{ borderColor: '#E4B4A6' }}>
          <div className="px-4 py-3">
            <button
              onClick={() => handleBuyNow('sticky')}
              className="w-full text-white font-bold py-3 px-6 rounded-xl text-base shadow-lg transition-all"
              style={{
                background: '#413C04',
                fontFamily: 'Rubik, sans-serif'
              }}
            >
              Obtener Mi Tour — $26.99
            </button>
            <div className="flex items-center justify-center gap-3 mt-2 text-xs" style={{ color: '#413C04', opacity: 0.7 }}>
              <span>Pago seguro con Stripe</span>
              <span>•</span>
              <span>Reembolso 30 días</span>
            </div>
          </div>
        </div>
      )}

      {/* Exit Intent Popup */}
      {showExitPopup && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: 'rgba(0, 0, 0, 0.6)' }}>
          <div
            className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
            style={{ animation: 'popupSlideIn 0.3s ease-out' }}
          >
            {/* Close button */}
            <button
              onClick={handlePopupClose}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors z-10"
              aria-label="Cerrar"
            >
              <X size={20} style={{ color: '#413C04' }} />
            </button>

            {/* Header with gradient */}
            <div className="pt-8 pb-6 px-6 text-center" style={{ background: 'linear-gradient(to bottom right, #FFFADF, rgba(228, 180, 166, 0.3))' }}>
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: '#F8662B' }}>
                <Gift size={32} style={{ color: 'white' }} />
              </div>
              <div className="inline-block px-3 py-1 rounded-full text-xs font-bold mb-3" style={{ background: '#385B37', color: 'white' }}>
                OFERTA POR TIEMPO LIMITADO
              </div>
              <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                ¡Obtén <span style={{ color: '#F8662B' }}>20% de Descuento</span>!
              </h3>
              <p style={{ color: '#413C04', opacity: 0.8 }}>
                ¡Duplica el ahorro! Ingresa tu email para un código exclusivo.
              </p>
            </div>

            {/* Form content */}
            <div className="p-6">
              {!popupSuccess ? (
                <form onSubmit={handlePopupSubmit}>
                  <div className="mb-4">
                    <input
                      type="email"
                      value={popupEmail}
                      onChange={(e) => setPopupEmail(e.target.value)}
                      placeholder="Ingresa tu email"
                      required
                      className="w-full px-4 py-3 rounded-xl border-2 focus:outline-none focus:border-orange-400 transition-colors"
                      style={{ borderColor: '#E4B4A6', fontFamily: 'Urbanist, sans-serif' }}
                    />
                  </div>

                  {popupError && (
                    <p className="text-red-500 text-sm mb-4 text-center">{popupError}</p>
                  )}

                  <button
                    type="submit"
                    disabled={popupSubmitting}
                    className="w-full text-white font-bold py-4 px-8 rounded-xl text-lg shadow-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      background: '#F8662B',
                      fontFamily: 'Rubik, sans-serif'
                    }}
                  >
                    {popupSubmitting ? 'Cargando...' : 'Obtener Mi Descuento'}
                  </button>
                </form>
              ) : (
                <div className="text-center py-4">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(56, 91, 55, 0.15)' }}>
                    <Check size={32} style={{ color: '#385B37' }} />
                  </div>
                  <h4 className="text-xl font-bold mb-2" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                    ¡Aquí Está Tu Código!
                  </h4>
                  <p className="mb-4" style={{ color: '#413C04', opacity: 0.8 }}>
                    Usa este código al pagar para 20% de descuento:
                  </p>

                  {/* Promo code box with copy button */}
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <div
                      className="px-6 py-3 rounded-lg font-bold text-xl tracking-wider"
                      style={{
                        background: 'linear-gradient(to right, #FFFADF, rgba(228, 180, 166, 0.3))',
                        border: '2px dashed #F8662B',
                        fontFamily: 'Rubik, sans-serif',
                        color: '#413C04'
                      }}
                    >
                      EXPLORE2026
                    </div>
                    <button
                      onClick={handleCopyCode}
                      className="px-4 py-3 rounded-lg font-semibold transition-all"
                      style={{
                        background: codeCopied ? '#385B37' : '#F8662B',
                        color: 'white',
                        fontFamily: 'Urbanist, sans-serif'
                      }}
                    >
                      {codeCopied ? '¡Copiado!' : 'Copiar'}
                    </button>
                  </div>

                  <button
                    onClick={() => handleBuyNow('popup')}
                    className="w-full text-white font-bold py-3 px-6 rounded-xl text-base shadow-lg transition-all transform hover:scale-105 mb-3"
                    style={{
                      background: '#413C04',
                      fontFamily: 'Rubik, sans-serif'
                    }}
                  >
                    Usar Código Ahora
                  </button>

                  <button
                    onClick={handlePopupClose}
                    className="text-sm font-semibold underline"
                    style={{ color: '#F8662B' }}
                  >
                    Seguir navegando
                  </button>
                </div>
              )}
            </div>
          </div>

          <style>{`
            @keyframes popupSlideIn {
              from {
                opacity: 0;
                transform: translateY(20px) scale(0.95);
              }
              to {
                opacity: 1;
                transform: translateY(0) scale(1);
              }
            }
          `}</style>
        </div>
      )}

    </div>
    </>
  );
}
